import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import EventsSlider from '../components/EventsSlider';
import { useFeaturedEvents } from '../hooks/useFeaturedEvents';
import backgroundImage from '../assets/login-background.jpg';

/* ─── Theme constants ─── */
const T = {
  primary:    '#FF9500',
  primaryDk:  '#E07800',
  dark:       '#1c1a17',
  darkSoft:   '#2a2620',
  text:       '#2d2a26',
  textMid:    '#5a5550',
  textLight:  '#ffffff',
  light:      '#f7f5f2',
  grey:       '#ede9e4',
  cardShadow: '0 8px 28px rgba(0,0,0,0.07)',
  radius:     '14px',
  radiusLg:   '20px',
};

/* ─── Keyframes ─── */
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(28px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ─── Framer variants ─── */
const fadeUp = {
  hidden:  { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] } }),
};
const fadeLeft = {
  hidden:  { opacity: 0, x: -40 },
  visible: (i = 0) => ({
    opacity: 1,
    x: 0,
    transition: { duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }
  }),
};
const fadeRight = {
  hidden:  { opacity: 0, x: 40 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

/* ─── Layout ─── */
const LandingContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: ${T.text};
  overflow-x: hidden;
  background: #fff;
`;

/* ══════════════════════════════════════
   NAVBAR
══════════════════════════════════════ */
const NavBar = styled(motion.nav)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  padding: 0 32px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background 0.35s ease, backdrop-filter 0.35s ease,
              box-shadow 0.35s ease, border-bottom 0.35s ease;

  background: ${({ $scrolled }) =>
    $scrolled ? 'rgba(20, 18, 14, 0.97)' : 'transparent'};
  backdrop-filter: ${({ $scrolled }) =>
    $scrolled ? 'blur(14px)' : 'none'};
  box-shadow: ${({ $scrolled }) =>
    $scrolled ? '0 2px 20px rgba(0,0,0,0.35)' : 'none'};
  border-bottom: ${({ $scrolled }) =>
    $scrolled ? '1px solid rgba(255,255,255,0.06)' : 'none'};

  @media (max-width: 768px) { padding: 0 20px; }
`;

const NavLogo = styled.a`
  display: flex;
  align-items: center;
  text-decoration: none;
  flex-shrink: 0;

  img {
    height: 28px;
    width: auto;
    filter: brightness(0) invert(1);
    transition: opacity 0.2s ease;
  }

  &:hover img { opacity: 0.85; }
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  @media (max-width: 768px) { display: none; }
`;

const NavLink = styled.a`
  font-family: 'DM Sans', sans-serif;
  font-size: 0.88rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.82);
  text-decoration: none;
  padding: 8px 14px;
  border-radius: 8px;
  transition: color 0.2s ease, background 0.2s ease;
  cursor: pointer;

  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.08);
  }
`;

const NavCTA = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, ${T.primary}, ${T.primaryDk});
  color: white;
  padding: 9px 22px;
  border-radius: 50px;
  font-family: 'DM Sans', sans-serif;
  font-weight: 700;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  text-decoration: none;
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(255, 149, 0, 0.35);
  transition: transform 0.22s ease, box-shadow 0.22s ease;
  white-space: nowrap;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(255, 149, 0, 0.5);
  }

  @media (max-width: 768px) { display: none; }
`;

const HamburgerBtn = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  color: white;

  @media (max-width: 768px) { display: flex; align-items: center; }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 64px;
  left: 0;
  right: 0;
  z-index: 99;
  background: rgba(20, 18, 14, 0.97);
  backdrop-filter: blur(14px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  padding: 16px 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MobileNavLink = styled.a`
  font-family: 'DM Sans', sans-serif;
  font-size: 1rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
  text-decoration: none;
  padding: 13px 16px;
  border-radius: 10px;
  transition: background 0.2s ease, color 0.2s ease;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.07);
    color: white;
  }
`;

const MobileNavCTA = styled(NavCTA)`
  display: inline-flex;
  margin-top: 8px;
  justify-content: center;
`;

/* ══════════════════════════════════════
   HERO
══════════════════════════════════════ */
const HeroSection = styled.section`
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      linear-gradient(160deg, rgba(0,0,0,0.78) 0%, rgba(28,12,0,0.62) 60%, rgba(0,0,0,0.55) 100%),
      url(${backgroundImage}) center/cover no-repeat;
    z-index: 0;
  }

  @media (max-width: 768px) {
    min-height: auto;
    padding: 130px 0 90px;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 820px;
  margin: 0 auto;
  padding: 0 32px;
  text-align: center;
  color: ${T.textLight};
`;

const HeroBadge = styled(motion.span)`
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 2.5px;
  color: ${T.primary};
  background: rgba(255, 149, 0, 0.15);
  border: 1px solid rgba(255, 149, 0, 0.35);
  padding: 6px 18px;
  border-radius: 30px;
  margin-bottom: 22px;
`;

const HeroTitle = styled(motion.h1)`
  font-size: clamp(2.4rem, 6vw, 4rem);
  font-weight: 800;
  letter-spacing: -2px;
  line-height: 1.08;
  margin-bottom: 18px;
  text-shadow: 0 2px 16px rgba(0,0,0,0.3);

  span { color: ${T.primary}; }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: clamp(1rem, 2.5vw, 1.18rem);
  font-weight: 400;
  line-height: 1.65;
  opacity: 0.88;
  max-width: 620px;
  margin: 0 auto 36px;
  text-shadow: 0 1px 6px rgba(0,0,0,0.2);
`;

const HeroButtons = styled(motion.div)`
  display: flex;
  gap: 14px;
  justify-content: center;
  flex-wrap: wrap;
`;

const HeroBtnPrimary = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, ${T.primary}, ${T.primaryDk});
  color: white;
  padding: 15px 34px;
  border-radius: 50px;
  font-family: inherit;
  font-weight: 700;
  font-size: 0.92rem;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  border: none;
  cursor: pointer;
  box-shadow: 0 6px 24px rgba(255,149,0,0.4);
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(255,149,0,0.5);
  }

  svg { width: 16px; height: 16px; }
`;

const HeroBtnSecondary = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: rgba(255,255,255,0.12);
  backdrop-filter: blur(8px);
  color: white;
  padding: 15px 34px;
  border-radius: 50px;
  font-family: inherit;
  font-weight: 600;
  font-size: 0.92rem;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  border: 1px solid rgba(255,255,255,0.3);
  cursor: pointer;
  transition: all 0.25s ease;

  &:hover {
    background: rgba(255,255,255,0.2);
    transform: translateY(-2px);
  }
`;

const HeroScrollIndicator = styled(motion.div)`
  position: absolute;
  bottom: 32px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  color: rgba(255,255,255,0.55);
  font-size: 0.72rem;
  letter-spacing: 1.5px;
  text-transform: uppercase;

  svg {
    width: 20px; height: 20px;
    animation: ${fadeInUp} 1.2s ease infinite alternate;
  }

  @media (max-width: 768px) { display: none; }
`;
/* ══════════════════════════════════════
   SHARED SECTION ATOMS
══════════════════════════════════════ */
const SectionWrap = styled.section`
  padding: ${({ $tight }) => $tight ? '70px 32px' : '90px 32px'};
  background: ${({ $bg }) => $bg || '#fff'};

  @media (max-width: 768px) { padding: 60px 20px; }
`;

const Container = styled.div`
  max-width: 1180px;
  margin: 0 auto;
`;

const SectionTag = styled(motion.span)`
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 2.5px;
  color: ${T.primary};
  background: rgba(255, 149, 0, 0.08);
  padding: 6px 16px;
  border-radius: 20px;
  margin-bottom: 14px;
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(1.7rem, 4vw, 2.5rem);
  font-weight: 700;
  color: ${({ $light }) => $light ? T.textLight : T.text};
  letter-spacing: -0.5px;
  line-height: 1.2;
  margin-bottom: 14px;
`;

const SectionDesc = styled(motion.p)`
  font-size: 1.05rem;
  color: ${({ $light }) => $light ? 'rgba(255,255,255,0.75)' : T.textMid};
  line-height: 1.7;
  max-width: 600px;
  margin-bottom: 48px;

  &.centered {
    margin-left: auto;
    margin-right: auto;
    text-align: center;
  }
`;

/* ══════════════════════════════════════
   FEATURES SECTION
══════════════════════════════════════ */
const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 22px;

  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const FeatureCard = styled(motion.div)`
  display: flex;
  align-items: flex-start;
  gap: 18px;
  background: white;
  padding: 28px;
  border-radius: ${T.radius};
  box-shadow: ${T.cardShadow};
  border: 1px solid ${T.grey};
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 14px 38px rgba(0,0,0,0.11);
  }
`;

const FeatureIconBox = styled.div`
  width: 54px;
  height: 54px;
  border-radius: 14px;
  background: ${({ $bg }) => $bg || 'rgba(255,149,0,0.1)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  flex-shrink: 0;
`;

const FeatureBody = styled.div`
  flex: 1;

  h3 {
    font-size: 1rem;
    font-weight: 700;
    color: ${T.text};
    margin-bottom: 6px;
  }
  p {
    font-size: 0.88rem;
    color: ${T.textMid};
    line-height: 1.6;
  }
`;

/* ══════════════════════════════════════
   HOW IT WORKS
══════════════════════════════════════ */
const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;

  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const StepCard = styled(motion.div)`
  background: white;
  padding: 30px 24px;
  border-radius: ${T.radius};
  box-shadow: ${T.cardShadow};
  text-align: center;
  border: 1px solid ${T.grey};
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 16px 40px rgba(0,0,0,0.1);
  }
`;

const StepIconCircle = styled.div`
  width: 68px;
  height: 68px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${T.primary}, ${T.primaryDk});
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.6rem;
  margin: 0 auto 16px;
  box-shadow: 0 6px 18px rgba(255,149,0,0.35);
`;

const StepTitle = styled.h3`
  font-size: 0.98rem;
  font-weight: 700;
  color: ${T.text};
  margin-bottom: 8px;
`;

const StepDesc = styled.p`
  font-size: 0.84rem;
  color: ${T.textMid};
  line-height: 1.6;
`;

/* ══════════════════════════════════════
   EVENTS SECTION
══════════════════════════════════════ */
const EventsSection = styled(SectionWrap)``;

const EventsHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 32px;
`;

const EventsTitle = styled.h2`
  font-size: 1.9rem;
  font-weight: 700;
  color: ${T.text};
  letter-spacing: -0.5px;
`;

/* ══════════════════════════════════════
   ORGANIZER SECTION
══════════════════════════════════════ */
const OrgSection = styled(SectionWrap)`
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${T.dark};
  }
`;

const OrgInner = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 60px;

  @media (max-width: 900px) {
    flex-direction: column;
    text-align: center;
  }
`;

const OrgText = styled.div`
  flex: 1;
`;

const OrgLabel = styled.span`
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 2.5px;
  color: ${T.primary};
  background: rgba(255, 149, 0, 0.15);
  border: 1px solid rgba(255, 149, 0, 0.3);
  padding: 6px 16px;
  border-radius: 20px;
  margin-bottom: 14px;
`;

const BetaBadge = styled.span`
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 700;
  color: white;
  background: ${T.primary};
  padding: 4px 10px;
  border-radius: 6px;
  margin-left: 10px;
  vertical-align: middle;
`;

const OrgTitle = styled(motion.h2)`
  font-size: clamp(1.6rem, 3.5vw, 2.2rem);
  font-weight: 700;
  color: white;
  letter-spacing: -0.5px;
  line-height: 1.25;
  margin-bottom: 16px;
`;

const OrgDesc = styled(motion.p)`
  font-size: 1rem;
  color: rgba(255,255,255,0.72);
  line-height: 1.7;
  margin-bottom: 28px;
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 32px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  @media (max-width: 900px) {
    align-items: center;
  }
`;

const BenefitItem = styled(motion.li)`
  display: flex;
  align-items: center;
  gap: 10px;
  color: rgba(255,255,255,0.88);
  font-size: 0.95rem;
  font-weight: 500;
`;

const BenefitCheck = styled.span`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(255, 149, 0, 0.2);
  border: 1px solid rgba(255, 149, 0, 0.5);
  color: ${T.primary};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.72rem;
  font-weight: 700;
  flex-shrink: 0;
`;

const OrgCTA = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, ${T.primary}, ${T.primaryDk});
  color: white;
  padding: 15px 34px;
  border-radius: 50px;
  font-family: inherit;
  font-weight: 700;
  font-size: 0.92rem;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  text-decoration: none;
  box-shadow: 0 6px 24px rgba(255,149,0,0.4);
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(255,149,0,0.5);
  }

  svg { width: 16px; height: 16px; }
`;

const OrgVisual = styled(motion.div)`
  flex-shrink: 0;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: rgba(255,149,0,0.1);
  border: 2px solid rgba(255,149,0,0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 5rem;

  @media (max-width: 900px) { width: 140px; height: 140px; font-size: 3.5rem; }
`;

/* ══════════════════════════════════════
   FOOTER
══════════════════════════════════════ */
const FooterEl = styled.footer`
  background: #111;
  color: rgba(255,255,255,0.8);
  padding: 60px 32px 32px;
`;

const FooterGrid = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 1fr;
  gap: 40px;

  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 500px) { grid-template-columns: 1fr; }
`;

const FooterCol = styled.div``;

const FooterLogo = styled.div`
  margin-bottom: 16px;
  img { max-width: 120px; filter: brightness(0) invert(1); }
`;

const FooterAbout = styled.p`
  font-size: 0.88rem;
  line-height: 1.65;
  color: rgba(255,255,255,0.55);
`;

const FooterColTitle = styled.h4`
  font-size: 0.8rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: ${T.primary};
  margin-bottom: 16px;
`;

const FooterLink = styled.a`
  display: block;
  font-size: 0.88rem;
  color: rgba(255,255,255,0.6);
  text-decoration: none;
  margin-bottom: 9px;
  transition: color 0.2s ease;

  &:hover { color: ${T.primary}; }
`;

const SocialRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 4px;
`;

const SocialBtn = styled.a`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: rgba(255,255,255,0.08);
  color: rgba(255,255,255,0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  font-size: 1rem;
  transition: all 0.2s ease;

  &:hover {
    background: ${T.primary};
    color: white;
    transform: translateY(-2px);
  }
`;

const FooterDivider = styled.hr`
  max-width: 1180px;
  margin: 36px auto 20px;
  border: none;
  border-top: 1px solid rgba(255,255,255,0.08);
`;

const FooterBottom = styled.p`
  max-width: 1180px;
  margin: 0 auto;
  text-align: center;
  font-size: 0.8rem;
  color: rgba(255,255,255,0.35);
`;
/* ══════════════════════════════════════
   DATA
══════════════════════════════════════ */
const features = [
  { icon: '📍', bg: 'rgba(255,149,0,0.1)',  title: 'Mappa interattiva',   desc: 'Visualizza tutti gli eventi su una mappa intuitiva. Filtra per zona, data e categoria.' },
  { icon: '🎭', bg: 'rgba(76,175,80,0.1)',   title: 'Ogni genere',         desc: 'Concerti, teatro, sagre, mostre, sport, eventi per bambini e famiglie — c\'è tutto.' },
  { icon: '⚡', bg: 'rgba(33,150,243,0.1)',  title: 'Sempre aggiornato',   desc: 'Nuovi eventi ogni giorno, aggiunti dagli organizzatori stessi. Niente sorprese.' },
  { icon: '🔔', bg: 'rgba(156,39,176,0.1)',  title: 'Notifiche smart',     desc: 'Ricevi aggiornamenti sugli eventi che ti interessano, così non perdi niente.' },
];

const steps = [
  { icon: '🗺️', title: 'Esplora la mappa',      desc: 'Scopri tutti gli eventi vicino a te con un colpo d\'occhio sulla mappa interattiva.' },
  { icon: '🔍', title: 'Filtra per interesse',   desc: 'Seleziona categoria, data e distanza per trovare esattamente quello che cerchi.' },
  { icon: 'ℹ️', title: 'Trova informazioni',     desc: 'Accedi a orari, costi, descrizioni e tutte le info utili di ogni evento.' },
  { icon: '🎉', title: 'Partecipa e divertiti',  desc: 'Raggiungi la destinazione e vivi nuove esperienze ogni giorno in Liguria.' },
];

const benefits = [
  'Raggiungi un pubblico più ampio e mirato',
  'Gestisci facilmente le tue inserzioni',
  'Ottieni insight sulle tue performance',
  'Aumenta la visibilità dei tuoi eventi',
];

/* ══════════════════════════════════════
   COMPONENT
══════════════════════════════════════ */
const ArrowIcon = React.memo(() => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
));

function LandingPage() {
  const navigate = useNavigate();
  const { events, loading, error } = useFeaturedEvents();

  const handleLoadApp = () => navigate('/app');

  /* ── Scroll state ── */
  const [scrolled,   setScrolled]   = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* ── Analytics ── */
  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title:    'Landing Page',
        page_location: window.location.href,
      });
    }
  }, []);

  /* ── Refs for inView sections ── */
  const featuresRef = useRef(null);
  const stepsRef    = useRef(null);
  const orgRef      = useRef(null);
  const featuresInView = useInView(featuresRef, { once: true, amount: 0.15 });
  const stepsInView    = useInView(stepsRef,    { once: true, amount: 0.15 });
  const orgInView      = useInView(orgRef,      { once: true, amount: 0.15 });

  /* ─── Render ─── */
  return (
    <>
      <LandingContainer>

        {/* ══ NAVBAR ══ */}
        <NavBar $scrolled={scrolled}>
          <NavLogo href="/">
            <img src="/logo.png" alt="Finday" />
          </NavLogo>

          <NavLinks>
            <NavLink
              onClick={() =>
                document.getElementById('how-it-works')
                  ?.scrollIntoView({ behavior: 'smooth' })
              }
            >
              Come funziona
            </NavLink>
            <NavLink
              onClick={() =>
                document.getElementById('organizers')
                  ?.scrollIntoView({ behavior: 'smooth' })
              }
            >
              Per organizzatori
            </NavLink>
            <NavLink href="mailto:info@finday.it">Contatti</NavLink>
          </NavLinks>

          <NavCTA href="/app">
            Apri la mappa <ArrowIcon />
          </NavCTA>

          <HamburgerBtn
            type="button"
            aria-label="Menu"
            onClick={() => setMobileOpen(o => !o)}
          >
            {mobileOpen ? (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2.5"
                   strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            ) : (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2.5"
                   strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </HamburgerBtn>
        </NavBar>

        {/* ══ MOBILE MENU ══ */}
        <AnimatePresence>
          {mobileOpen && (
            <MobileMenu
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.22 }}
            >
              <MobileNavLink
                onClick={() => {
                  document.getElementById('how-it-works')
                    ?.scrollIntoView({ behavior: 'smooth' });
                  setMobileOpen(false);
                }}
              >
                Come funziona
              </MobileNavLink>
              <MobileNavLink
                onClick={() => {
                  document.getElementById('organizers')
                    ?.scrollIntoView({ behavior: 'smooth' });
                  setMobileOpen(false);
                }}
              >
                Per organizzatori
              </MobileNavLink>
              <MobileNavLink href="mailto:info@finday.it">
                Contatti
              </MobileNavLink>
              <MobileNavCTA href="/app">
                Apri la mappa <ArrowIcon />
              </MobileNavCTA>
            </MobileMenu>
          )}
        </AnimatePresence>

        {/* ══ HERO ══ */}
        <HeroSection>
          <HeroContent>
            <HeroBadge
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              Scopri la Liguria
            </HeroBadge>

            <HeroTitle
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
            >
              Trova nuovi eventi<br /><span>ogni giorno</span>
            </HeroTitle>

            <HeroSubtitle
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              La piattaforma che ti connette con concerti, mostre, sagre ed
              eventi sportivi e molto altro in Liguria. Da Imperia a La Spezia — tutto su una mappa.
            </HeroSubtitle>

            <HeroButtons
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.35 }}
            >
              <HeroBtnPrimary type="button" onClick={handleLoadApp}>
                Apri la mappa <ArrowIcon />
              </HeroBtnPrimary>
              <HeroBtnSecondary type="button" onClick={() => {
                document.getElementById('organizers')
                  ?.scrollIntoView({ behavior: 'smooth' });
              }}>
                Per organizzatori
              </HeroBtnSecondary>
            </HeroButtons>
          </HeroContent>

          <HeroScrollIndicator
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
          >
            <svg viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2">
              <path d="M7 10l5 5 5-5" />
            </svg>
            Scorri
          </HeroScrollIndicator>
        </HeroSection>

        {/* ══ FEATURES ══ */}
        <SectionWrap $bg="#fff" ref={featuresRef}>
          <Container>
            <SectionTag
              variants={fadeUp}
              initial="hidden"
              animate={featuresInView ? 'visible' : 'hidden'}
            >
              Funzionalità
            </SectionTag>
            <SectionTitle
              variants={fadeUp}
              custom={1}
              initial="hidden"
              animate={featuresInView ? 'visible' : 'hidden'}
            >
              Tutto quello che ti serve
            </SectionTitle>
            <SectionDesc
              variants={fadeUp}
              custom={2}
              initial="hidden"
              animate={featuresInView ? 'visible' : 'hidden'}
            >
              Concerti, spettacoli, festival e molto altro. Filtri avanzati,
              aggiornamenti in tempo reale e notifiche personalizzate.
            </SectionDesc>

            <FeaturesGrid>
              {features.map((f, i) => (
                <FeatureCard
                  key={i}
                  variants={fadeUp}
                  custom={i}
                  initial="hidden"
                  animate={featuresInView ? 'visible' : 'hidden'}
                >
                  <FeatureIconBox $bg={f.bg}>{f.icon}</FeatureIconBox>
                  <FeatureBody>
                    <h3>{f.title}</h3>
                    <p>{f.desc}</p>
                  </FeatureBody>
                </FeatureCard>
              ))}
            </FeaturesGrid>
          </Container>
        </SectionWrap>

        {/* ══ HOW IT WORKS ══ */}
        <SectionWrap $bg={T.light} ref={stepsRef} id="how-it-works">
          <Container style={{ textAlign: 'center' }}>
            <SectionTag
              variants={fadeUp}
              initial="hidden"
              animate={stepsInView ? 'visible' : 'hidden'}
              style={{ display: 'block', textAlign: 'center' }}
            >
              Come funziona
            </SectionTag>
            <SectionTitle
              variants={fadeUp}
              custom={1}
              initial="hidden"
              animate={stepsInView ? 'visible' : 'hidden'}
              style={{ textAlign: 'center' }}
            >
              In quattro passi semplici
            </SectionTitle>
            <SectionDesc
              variants={fadeUp}
              custom={2}
              initial="hidden"
              animate={stepsInView ? 'visible' : 'hidden'}
              className="centered"
            >
              Finday rende facile scoprire e partecipare agli eventi più
              interessanti vicino a te.
            </SectionDesc>

            <StepsGrid>
              {steps.map((s, i) => (
                <StepCard
                  key={i}
                  variants={fadeUp}
                  custom={i}
                  initial="hidden"
                  animate={stepsInView ? 'visible' : 'hidden'}
                >
                  <StepIconCircle>{s.icon}</StepIconCircle>
                  <StepTitle>{s.title}</StepTitle>
                  <StepDesc>{s.desc}</StepDesc>
                </StepCard>
              ))}
            </StepsGrid>
          </Container>
        </SectionWrap>

        {/* ══ EVENTS SLIDER ══ */}
        <EventsSection $bg="#fff">
          <Container>
            <EventsHeader>
              <EventsTitle>🎪 Eventi in evidenza</EventsTitle>
            </EventsHeader>
            <EventsSlider events={events} loading={loading} error={error} />
          </Container>
        </EventsSection>

        {/* ══ FOR ORGANIZERS ══ */}
        <OrgSection id="organizers" ref={orgRef}>
          <Container>
            <OrgInner>
              <OrgText>
                <div>
                  <OrgLabel>Per organizzatori</OrgLabel>
                  <BetaBadge>🚀 BETA</BetaBadge>
                </div>

                <OrgTitle
                  variants={fadeLeft}
                  initial="hidden"
                  animate={orgInView ? 'visible' : 'hidden'}
                >
                  Fai crescere il tuo<br />pubblico con Finday
                </OrgTitle>

                <OrgDesc
                  variants={fadeLeft}
                  custom={1}
                  initial="hidden"
                  animate={orgInView ? 'visible' : 'hidden'}
                >
                  Sei un organizzatore di eventi, un'associazione culturale o un locale?
                  Il portale è in beta e stiamo cercando i primi organizzatori che
                  vogliano pubblicare i propri eventi. Registrati gratuitamente!
                </OrgDesc>

                <BenefitsList>
                  {benefits.map((b, i) => (
                    <BenefitItem
                      key={i}
                      variants={fadeLeft}
                      custom={i + 2}
                      initial="hidden"
                      animate={orgInView ? 'visible' : 'hidden'}
                    >
                      <BenefitCheck>✓</BenefitCheck>
                      {b}
                    </BenefitItem>
                  ))}
                </BenefitsList>

                <OrgCTA
                  href="https://www.finday.it/admin"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Prova il portale in anteprima <ArrowIcon />
                </OrgCTA>
              </OrgText>

              <OrgVisual
                variants={fadeRight}
                initial="hidden"
                animate={orgInView ? 'visible' : 'hidden'}
              >
                🎪
              </OrgVisual>
            </OrgInner>
          </Container>
        </OrgSection>

        {/* ══ FOOTER ══ */}
        <FooterEl>
          <FooterGrid>
            <FooterCol>
              <FooterLogo>
                <img src="/logo.png" alt="Finday" />
              </FooterLogo>
              <FooterAbout>
                La piattaforma per la scoperta e la promozione degli eventi in Liguria.
                Concerti, festival, mostre e spettacoli nelle province di Genova,
                La Spezia, Savona e Imperia.
              </FooterAbout>
            </FooterCol>

            <FooterCol>
              <FooterColTitle>Link utili</FooterColTitle>
              <FooterLink href="/app">Mappa eventi</FooterLink>
              <FooterLink
                onClick={() =>
                  document.getElementById('organizers')
                    ?.scrollIntoView({ behavior: 'smooth' })
                }
                style={{ cursor: 'pointer' }}
              >
                Per organizzatori
              </FooterLink>
              <FooterLink
                href="https://www.finday.it/admin"
                target="_blank"
                rel="noopener noreferrer"
              >
                Portale organizzatori
              </FooterLink>
            </FooterCol>

            <FooterCol>
              <FooterColTitle>Risorse</FooterColTitle>
              <FooterLink href="/privacy">Privacy Policy</FooterLink>
              <FooterLink href="/cookie-policy">Cookie Policy</FooterLink>
              <FooterLink href="/terms">Termini di Servizio</FooterLink>
            </FooterCol>

            <FooterCol>
              <FooterColTitle>Contatti</FooterColTitle>
              <FooterLink href="mailto:info@finday.it">info@finday.it</FooterLink>
              <SocialRow>
                <SocialBtn
                  href="https://www.facebook.com/findayapp"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                >
                  <i className="fab fa-facebook-f" />
                </SocialBtn>
                <SocialBtn
                  href="https://www.instagram.com/findayapp"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                >
                  <i className="fab fa-instagram" />
                </SocialBtn>
                <SocialBtn
                  href="https://twitter.com/findayapp"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Twitter"
                >
                  <i className="fab fa-twitter" />
                </SocialBtn>
              </SocialRow>
            </FooterCol>
          </FooterGrid>

          <FooterDivider />
          <FooterBottom>
            © {new Date().getFullYear()} Finday. Tutti i diritti riservati. — Liguria, Italia
          </FooterBottom>
        </FooterEl>

      </LandingContainer>
    </>
  );
}

export default LandingPage;
